import React from 'react';
import './VoicePermissionModal.scss';
import { DisableVoiceIcon } from '@assets/icon';

export type VoicePermissionErrorType =
  | 'PERMISSION_DENIED'
  | 'MICROPHONE_NOT_FOUND'
  | 'BROWSER_NOT_SUPPORTED';

export interface VoicePermissionModalProps {
  open: boolean;
  type: VoicePermissionErrorType | null;
  onClose: () => void;
  onConfirm?: () => void;
  onRetry?: () => void;
}

interface ModalContentMeta {
  title: string;
  description: string;
  isTwoButtons: boolean;
  cancelText?: string;
  confirmText: string;
}

const MODAL_META_BY_TYPE: Partial<Record<VoicePermissionErrorType, ModalContentMeta>> = {
  MICROPHONE_NOT_FOUND: {
    title: 'Không tìm thấy micro',
    description: 'Vui lòng kết nối micro hoặc tai nghe có micro rồi thử lại. Bạn vẫn có thể nhập tin nhắn để tiếp tục.',
    isTwoButtons: true,
    cancelText: 'Đóng',
    confirmText: 'Thử lại',
  },
  BROWSER_NOT_SUPPORTED: {
    title: 'Trình duyệt không hỗ trợ ghi âm',
    description: 'Vui lòng mở Tendoo AI trên Chrome hoặc Safari phiên bản mới nhất để trò chuyện bằng giọng nói. Bạn vẫn có thể nhập tin nhắn để tiếp tục.',
    isTwoButtons: false,
    confirmText: 'Đã hiểu',
  },
};

const getModalContentMeta = (type: VoicePermissionErrorType | null): ModalContentMeta | null => {
  if (!type) return null;
  return MODAL_META_BY_TYPE[type] || null;
};

const ModalIcon: React.FC = () => (
  <DisableVoiceIcon className="voice-permission-modal-icon" width={48} height={48} />
);

interface ActionsProps {
  meta: ModalContentMeta;
  onCancel: () => void;
  onConfirm: () => void;
}

const getActionsClass = (isTwoButtons?: boolean) =>
  `voice-permission-modal-actions ${isTwoButtons ? 'has-two-buttons' : 'has-single-button'}`;

const ModalActions: React.FC<ActionsProps> = ({ meta, onCancel, onConfirm }) => (
  <div className={getActionsClass(meta?.isTwoButtons)}>
    {meta?.isTwoButtons && (
      <button type="button" className="voice-permission-modal-btn-cancel" onClick={onCancel}>
        {meta?.cancelText}
      </button>
    )}
    <button type="button" className="voice-permission-modal-btn-confirm" onClick={onConfirm}>
      {meta?.confirmText}
    </button>
  </div>
);

const handleModalConfirm = (
  type: VoicePermissionErrorType,
  onClose?: () => void,
  onRetry?: () => void
) => {
  onClose?.();
  if (type === 'MICROPHONE_NOT_FOUND') onRetry?.();
};

interface ModalCardProps {
  meta: ModalContentMeta;
  onCancel: () => void;
  onConfirm: () => void;
}

const ModalCard: React.FC<ModalCardProps> = ({ meta, onCancel, onConfirm }) => (
  <div className="voice-permission-modal-card">
    <ModalIcon />
    <h3 className="voice-permission-modal-title">{meta.title}</h3>
    <p className="voice-permission-modal-desc">{meta.description}</p>
    <ModalActions meta={meta} onCancel={onCancel} onConfirm={onConfirm} />
  </div>
);

export const VoicePermissionModal: React.FC<VoicePermissionModalProps> = ({
  open,
  type,
  onClose,
  onRetry,
}) => {
  if (!open || !type || type === 'PERMISSION_DENIED') return null;
  const meta = getModalContentMeta(type);
  if (!meta) return null;

  return (
    <div className="voice-permission-modal-overlay" role="dialog" aria-modal="true">
      <ModalCard
        meta={meta}
        onCancel={onClose}
        onConfirm={() => handleModalConfirm(type, onClose, onRetry)}
      />
    </div>
  );
};

export default VoicePermissionModal;
